import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@medikei/ui';
import {
  Users,
  Clock,
  Check,
  X,
  User,
  Zap,
  AlertCircle,
  Bell,
  BellRing,
  Phone,
  ShieldAlert,
  Lock,
} from 'lucide-react';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { useDoctorExpressSocket, InvitationData } from '@/hooks/express';
import { useWebPush, useAuth } from '@/hooks';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { DoctorOnboardingModal } from '@/components/doctor';
import { doctorQueueApi, AcceptInvitationResponse } from '@/api';
import { playIncomingCallRingtone, stopIncomingCallRingtone } from '@/lib/sound';
import { tabAlertManager } from '@/lib/tab-alert-manager';
import { useDoctorQueueStore } from '@/stores/useDoctorQueueStore';
import { useToast } from '@/components/ui/use-toast';

interface QueueStatus {
  waitingCount: number;
  inProgressCount: number;
  availableDoctors: number;
  averageWaitTime: number;
}

interface ExpressInvitation {
  invitationId: string;
  expressId: string;
  specialtyName?: string;
  patientName?: string;
  summaryAI?: unknown;
  riskClassification?: string;
  expiresAt: string;
}

export function DoctorQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: profile } = useDoctorProfile(user?.id || '');
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const doctorStatus = profile?.status || (user as any)?.doctorStatus || 'PENDING';
  const isApproved = doctorStatus === 'APPROVED';

  const {
    isOnline,
    setIsOnline,
    fetchStatus,
    toggleOnline,
    currentInvitation: storeInvitation,
    setCurrentInvitation: setStoreInvitation,
  } = useDoctorQueueStore();

  const currentInvitation = storeInvitation as ExpressInvitation | null;
  const setCurrentInvitation = setStoreInvitation as (inv: ExpressInvitation | null) => void;

  const [invitationCountdown, setInvitationCountdown] = useState<number>(30);
  const [dismissPushBanner, setDismissPushBanner] = useState(false);

  // Carrega status inicial do médico ao montar ou alterar aprovação
  useEffect(() => {
    if (!isApproved) {
      setIsOnline(false);
      return;
    }
    fetchStatus();
  }, [isApproved, fetchStatus, setIsOnline]);

  const {
    permission,
    isSubscribed,
    isSupported: isPushSupported,
    subscribeToPush,
    isLoading: isPushLoading,
  } = useWebPush();

  const token = localStorage.getItem('accessToken') || undefined;

  const {
    connect,
    disconnect,
    onNewInvitation,
    onInvitationCancelled,
  } = useDoctorExpressSocket({ token, enabled: isOnline });

  useEffect(() => {
    console.info('[DOCTOR-QUEUE-FE] DoctorQueuePage montada. Status do médico:', {
      isOnline,
      hasToken: Boolean(token),
      connected: Boolean(token && isOnline),
    });

    if (!isOnline) return;

    const unsubscribeNewInvitation = onNewInvitation((data: InvitationData) => {
      console.info('[DOCTOR-QUEUE-FE] 🔴 Convite de consulta recebido via Socket:', data);
      const initialRemaining = data.expiresAt ? Math.max(1, Math.floor((new Date(data.expiresAt).getTime() - Date.now()) / 1000)) : 30;
      setInvitationCountdown(initialRemaining);
      setCurrentInvitation({
        invitationId: data.invitationId,
        expressId: data.expressId,
        specialtyName: data.specialtyName,
        patientName: data.patientName,
        summaryAI: data.summaryAI,
        riskClassification: data.riskClassification,
        expiresAt: data.expiresAt,
      });

      // Gerenciador de alerta na aba com áudio sintético contínuo e título alternante caso a aba esteja em segundo plano
      if (typeof document !== 'undefined' && document.hidden) {
        tabAlertManager.startAlert('🔴 NOVA CHAMADA!');
      } else {
        try {
          playIncomingCallRingtone();
        } catch (e) {
          console.warn('[DoctorQueue] Failed to play ringtone:', e);
        }
      }
    });

    const unsubscribeCancelled = onInvitationCancelled(() => {
      tabAlertManager.stopAlert();
      stopIncomingCallRingtone();
      setCurrentInvitation(null);
      setInvitationCountdown(0);
    });

    return () => {
      unsubscribeNewInvitation();
      unsubscribeCancelled();
      tabAlertManager.stopAlert();
      stopIncomingCallRingtone();
    };
  }, [isOnline, onNewInvitation, onInvitationCancelled]);

  const { data: queueStatus, isLoading, isError, refetch } = useQuery<QueueStatus>({
    queryKey: ['doctor', 'queue', 'status'],
    queryFn: () => doctorQueueApi.getQueueStatus(),
    refetchInterval: 5000,
  });

  // Polling de segurança de convites pendentes (redundância garantida caso o WebSocket oscile)
  useQuery({
    queryKey: ['doctor', 'queue', 'pending-invitation'],
    queryFn: async () => {
      if (!isOnline || currentInvitation) return null;
      const inv = await doctorQueueApi.getPendingInvitations();
      if (inv && inv.invitationId && !currentInvitation) {
        console.info('[DOCTOR-QUEUE-FE] 🔔 Convite ativo capturado com sucesso:', inv);
        const initialRemaining = inv.expiresAt ? Math.max(1, Math.floor((new Date(inv.expiresAt).getTime() - Date.now()) / 1000)) : 30;
        setInvitationCountdown(initialRemaining);
        setCurrentInvitation({
          invitationId: inv.invitationId,
          expressId: inv.expressId,
          specialtyName: inv.specialtyName,
          patientName: inv.patientName,
          summaryAI: inv.summaryAI,
          riskClassification: inv.riskClassification,
          expiresAt: inv.expiresAt,
        });
        if (typeof document !== 'undefined' && document.hidden) {
          tabAlertManager.startAlert('🔴 NOVA CHAMADA!');
        } else {
          try {
            playIncomingCallRingtone();
          } catch (e) {
            console.warn('[DoctorQueue] Failed to play ringtone:', e);
          }
        }
      }
      return inv;
    },
    enabled: isOnline && !currentInvitation,
    refetchInterval: 3000,
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async () => {
      if (!isApproved) {
        setIsOnboardingOpen(true);
        return false;
      }
      return toggleOnline();
    },
    onSuccess: (online) => {
      if (!isApproved) return;
      if (online) {
        connect();
      } else {
        disconnect();
      }
      queryClient.invalidateQueries({ queryKey: ['doctor', 'queue'] });
    },
    onError: (error: any) => {
      console.error('[toggleStatus] Mutation error:', error);
      const message = error?.message || error?.error?.message || 'Não foi possível alterar o status do plantão.';
      toast({
        title: 'Plantão Indisponível',
        description: Array.isArray(message) ? message.join(', ') : message,
        variant: 'error',
      });
    },
  });

  const acceptInvitationMutation = useMutation<AcceptInvitationResponse, Error, string>({
    mutationFn: async (expressId: string) => {
      tabAlertManager.stopAlert();
      stopIncomingCallRingtone();
      return doctorQueueApi.acceptInvitation(expressId);
    },
    onSuccess: (data) => {
      tabAlertManager.stopAlert();
      stopIncomingCallRingtone();
      if (data.success && data.action === 'accepted') {
        setCurrentInvitation(null);
        setInvitationCountdown(0);
        queryClient.invalidateQueries({ queryKey: ['doctor', 'queue'] });
        if (data.sessionId) {
          navigate(`/medico/telemedicine/video/${data.sessionId}`);
        }
      } else {
        setCurrentInvitation(null);
        setInvitationCountdown(0);
        queryClient.invalidateQueries({ queryKey: ['doctor', 'queue'] });
        alert(data.message || 'Não foi possível aceitar a consulta.');
      }
    },
    onError: (error) => {
      tabAlertManager.stopAlert();
      stopIncomingCallRingtone();
      setCurrentInvitation(null);
      setInvitationCountdown(0);
      alert('Erro ao aceitar consulta: ' + (error.message || 'Tente novamente.'));
    },
  });

  const rejectInvitationMutation = useMutation<{ success: boolean }, Error, string>({
    mutationFn: async (expressId: string) => {
      tabAlertManager.stopAlert();
      stopIncomingCallRingtone();
      return doctorQueueApi.rejectInvitation(expressId);
    },
    onSuccess: () => {
      setCurrentInvitation(null);
      setInvitationCountdown(0);
    },
  });

  useEffect(() => {
    if (!currentInvitation) {
      setInvitationCountdown(30);
      return;
    }

    // Timer decrescente determinístico de 30 segundos no cliente, imune a diferenças de fuso horário
    setInvitationCountdown(30);
    const interval = setInterval(() => {
      setInvitationCountdown((prev) => {
        if (prev <= 1) {
          console.warn('[DoctorQueue] 30 segundos de convite esgotados');
          tabAlertManager.stopAlert();
          stopIncomingCallRingtone();
          setCurrentInvitation(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentInvitation?.invitationId]);

  const handleAccept = useCallback(() => {
    console.info('[DOCTOR-QUEUE-FE] Botão Aceitar Consulta clicado pelo médico!', {
      currentInvitation,
    });
    if (currentInvitation) {
      acceptInvitationMutation.mutate(currentInvitation.expressId);
    }
  }, [currentInvitation, acceptInvitationMutation]);

  const handleReject = useCallback(() => {
    console.info('[DOCTOR-QUEUE-FE] Botão Recusar clicado pelo médico!', {
      currentInvitation,
    });
    if (currentInvitation) {
      rejectInvitationMutation.mutate(currentInvitation.expressId);
    }
  }, [currentInvitation, rejectInvitationMutation]);

  // Listener para eventos disparados pelas ações da Notificação Push no Service Worker ou eventos de simulação E2E
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleCustomInvitation = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        setCurrentInvitation(customEvent.detail);
      }
    };

    window.addEventListener('medikei:express_invitation', handleCustomInvitation);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'EXPRESS_NEW_INVITATION' && event.data.invitation) {
        setCurrentInvitation(event.data.invitation);
      } else if (event.data?.type === 'EXPRESS_INVITATION_ACCEPTED') {
        if (currentInvitation && currentInvitation.expressId === event.data.expressId) {
          handleAccept();
        }
      } else if (event.data?.type === 'EXPRESS_INVITATION_DISMISSED') {
        if (currentInvitation && currentInvitation.expressId === event.data.expressId) {
          handleReject();
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('medikei:express_invitation', handleCustomInvitation);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [currentInvitation, handleAccept, handleReject]);

  if (isLoading) {
    return <LoadingOverlay message="Carregando fila..." />;
  }

  if (isError) {
    return <ErrorRetry message="Erro ao carregar status da fila" onRetry={() => refetch()} />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Alerta de compliance se o cadastro médico estiver em análise */}
      {!isApproved && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-amber-900 dark:text-amber-200 shadow-xs">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">
                Plantão Express bloqueado para cadastros médicos em análise
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Para receber chamadas de pacientes em tempo real, conclua o envio e aprovação dos seus documentos médicos.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsOnboardingOpen(true)}
            className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs h-9 px-4 rounded-xl shadow-xs transition-colors cursor-pointer"
          >
            Completar Documentação
          </button>
        </div>
      )}

      {/* Banner amigável para ativação de Notificações Web Push */}
      {isPushSupported && (permission === 'default' || (!isSubscribed && permission !== 'denied')) && !dismissPushBanner && (
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-primary/15 text-primary rounded-xl shrink-0 mt-0.5 sm:mt-0">
              <BellRing className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm">Ative alertas para não perder chamadas</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Receba notificações na área de trabalho mesmo com a aba minimizada ou navegando em outros sites.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button
              onClick={() => subscribeToPush()}
              disabled={isPushLoading}
              className="px-3.5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold rounded-xl shadow-xs transition-colors disabled:opacity-50"
            >
              {isPushLoading ? 'Ativando...' : 'Ativar Notificações'}
            </button>
            <button
              onClick={() => setDismissPushBanner(true)}
              className="p-2 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
              title="Dispensar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {currentInvitation && (
        <Card className="border-2 border-primary bg-primary/5 dark:bg-primary/10 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Bell className="h-5 w-5 animate-bounce" />
              Novo Paciente Esperando Atendimento!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-primary font-medium">Especialidade</p>
                <p className="text-lg font-bold text-foreground">{currentInvitation.specialtyName || 'Clínico Geral'}</p>
              </div>
              <div>
                <p className="text-sm text-primary font-medium">Paciente</p>
                <p className="text-lg font-bold text-foreground">{currentInvitation.patientName || 'Não identificado'}</p>
              </div>
              <div>
                <p className="text-sm text-primary font-medium">Tempo para aceitar</p>
                <p className="text-lg font-bold text-destructive">{invitationCountdown}s</p>
              </div>
              {currentInvitation.riskClassification && (
                <div>
                  <p className="text-sm text-primary font-medium">Classificação de Risco</p>
                  <p className="text-lg font-bold text-foreground">{currentInvitation.riskClassification}</p>
                </div>
              )}
            </div>

            {currentInvitation.summaryAI != null && (
              <div className="bg-card p-4 rounded-xl border border-border">
                <p className="text-sm font-medium text-foreground mb-2">Resumo da Triagem IA:</p>
                <p className="text-sm text-muted-foreground">{String(currentInvitation.summaryAI)}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleAccept}
                disabled={acceptInvitationMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl shadow-md disabled:opacity-50 transition-colors"
              >
                <Check className="h-5 w-5" />
                {acceptInvitationMutation.isPending ? 'Aceitando...' : 'Aceitar Consulta'}
              </button>
              <button
                onClick={handleReject}
                disabled={rejectInvitationMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-destructive hover:bg-destructive/90 text-white font-semibold rounded-xl shadow-md disabled:opacity-50 transition-colors"
              >
                <X className="h-5 w-5" />
                {rejectInvitationMutation.isPending ? 'Recusando...' : 'Recusar'}
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MODAL EM DESTAQUE MÁXIMO (OVERLAY) PARA GARANTIR VISIBILIDADE IMEDIATA */}
      {currentInvitation && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-200">
          <div className="w-full max-w-lg bg-card border-2 border-emerald-500 rounded-3xl p-6 shadow-2xl space-y-5 text-foreground">
            <div className="flex items-center justify-between pb-3 border-b">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-500 flex items-center justify-center animate-bounce">
                  <Bell className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-emerald-600 dark:text-emerald-400">Nova Chamada de Plantão!</h3>
                  <p className="text-xs text-muted-foreground">Paciente aguardando conexão imediata</p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-muted-foreground uppercase font-bold block">Expira em</span>
                <span className="text-2xl font-black text-rose-500">{invitationCountdown}s</span>
              </div>
            </div>

            <div className="space-y-3 bg-muted/40 p-4 rounded-2xl border border-border">
              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground font-medium">Especialidade:</span>
                <span className="font-bold text-foreground text-sm">{currentInvitation.specialtyName || 'Clínico Geral'}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-muted-foreground font-medium">Paciente:</span>
                <span className="font-bold text-foreground text-sm">{currentInvitation.patientName || 'Paciente'}</span>
              </div>
              {currentInvitation.riskClassification && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground font-medium">Classificação:</span>
                  <span className="px-2 py-0.5 rounded-full font-bold bg-amber-500/10 text-amber-600 border border-amber-500/20 uppercase text-[11px]">
                    {currentInvitation.riskClassification}
                  </span>
                </div>
              )}
              {Boolean(currentInvitation.summaryAI) && (() => {
                const summaryObj = typeof currentInvitation.summaryAI === 'object' && currentInvitation.summaryAI !== null
                  ? (currentInvitation.summaryAI as Record<string, any>)
                  : typeof currentInvitation.summaryAI === 'string'
                  ? (() => {
                      try {
                        const parsed = JSON.parse(currentInvitation.summaryAI);
                        return typeof parsed === 'object' && parsed !== null ? parsed : { summary: currentInvitation.summaryAI };
                      } catch {
                        return { summary: currentInvitation.summaryAI };
                      }
                    })()
                  : null;

                const mainSummary = summaryObj?.summary || summaryObj?.chiefComplaint || (typeof currentInvitation.summaryAI === 'string' ? currentInvitation.summaryAI : 'Paciente aguardando atendimento clínico.');

                return (
                  <div className="pt-2 border-t border-border space-y-2">
                    <span className="text-[11px] font-bold text-muted-foreground block">Triagem Clínica:</span>
                    <div className="bg-card p-3 rounded-xl border border-border space-y-2 text-xs text-foreground">
                      <p className="leading-relaxed italic">
                        &ldquo;{mainSummary}&rdquo;
                      </p>
                      {(summaryObj?.chiefComplaint || summaryObj?.symptomDuration || (summaryObj?.symptomIntensity !== undefined && summaryObj?.symptomIntensity !== null)) && (
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/50 text-[11px] text-muted-foreground">
                          {summaryObj?.chiefComplaint && (
                            <div>
                              <span className="font-semibold text-foreground">Queixa:</span> {summaryObj.chiefComplaint}
                            </div>
                          )}
                          {summaryObj?.symptomDuration && (
                            <div>
                              <span className="font-semibold text-foreground">Duração:</span> {summaryObj.symptomDuration}
                            </div>
                          )}
                          {summaryObj?.symptomIntensity !== undefined && summaryObj?.symptomIntensity !== null && (
                            <div>
                              <span className="font-semibold text-foreground">Intensidade:</span> {summaryObj.symptomIntensity}/10
                            </div>
                          )}
                          {summaryObj?.recommendedSpecialty && (
                            <div>
                              <span className="font-semibold text-foreground">Sugerida:</span> {summaryObj.recommendedSpecialty}
                            </div>
                          )}
                        </div>
                      )}
                      {Array.isArray(summaryObj?.redFlags) && summaryObj.redFlags.length > 0 && (
                        <div className="pt-1.5 flex flex-wrap gap-1 items-center">
                          <span className="text-[10px] font-bold text-rose-600 dark:text-rose-400">Sinais de alerta:</span>
                          {summaryObj.redFlags.map((flag: string, idx: number) => (
                            <span key={idx} className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                              {flag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleAccept}
                disabled={acceptInvitationMutation.isPending}
                className="flex-1 py-4 text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg shadow-emerald-600/30 gap-2 cursor-pointer"
              >
                <Check className="w-5 h-5 stroke-[3]" />
                <span>{acceptInvitationMutation.isPending ? 'Conectando...' : 'Aceitar e Iniciar Consulta'}</span>
              </Button>
              <Button
                onClick={handleReject}
                disabled={rejectInvitationMutation.isPending}
                variant="outline"
                className="px-5 text-xs text-muted-foreground hover:text-rose-600 border-border rounded-2xl"
              >
                Recusar
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Fila de Atendimento Expressa</h1>
          <p className="text-sm text-muted-foreground">Gerencie seus plantões e atenda pacientes que chegam em tempo real</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground/40'}`} />
            <span className="text-sm font-medium text-foreground">
              {!isApproved ? 'Offline (Aguardando Aprovação)' : isOnline ? 'Online - Recebendo pacientes' : 'Offline'}
            </span>
          </div>
          <button
            onClick={() => toggleStatusMutation.mutate()}
            disabled={toggleStatusMutation.isPending}
            className={`px-4 py-2 rounded-xl font-semibold transition-all shadow-xs disabled:opacity-50 cursor-pointer flex items-center gap-2 ${
              !isApproved
                ? 'bg-muted/70 text-muted-foreground border border-border hover:bg-muted opacity-80'
                : isOnline
                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20 border border-destructive/30'
                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30'
            }`}
          >
            {!isApproved && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
            <span>
              {toggleStatusMutation.isPending
                ? 'Atualizando...'
                : !isApproved
                ? 'Plantão Indisponível'
                : isOnline
                ? 'Ficar Offline'
                : 'Ficar Online'}
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-primary/10 text-primary rounded-xl">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Na Fila</p>
              <p className="text-2xl font-bold text-foreground">{queueStatus?.waitingCount || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-amber-500/10 text-amber-500 rounded-xl">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tempo Médio</p>
              <p className="text-2xl font-bold text-foreground">{queueStatus?.averageWaitTime || 0} min</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-emerald-500/10 text-emerald-500 rounded-xl">
              <User className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Médicos Online</p>
              <p className="text-2xl font-bold text-foreground">{queueStatus?.availableDoctors || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-purple-500/10 text-purple-500 rounded-xl">
              <Zap className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Em Atendimento</p>
              <p className="text-2xl font-bold text-foreground">{queueStatus?.inProgressCount || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <AlertCircle className="h-5 w-5 text-primary" />
            Como funciona a Consulta Expressa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2.5 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>Quando você estiver <strong>Online</strong>, receberá notificações automáticas de pacientes que aguardam atendimento.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>Você tem <strong>30 segundos</strong> para aceitar cada convite de consulta antes de ser repassado a outro colega.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>Ao aceitar, você e o paciente são direcionados para uma sala segura de teleconsulta com vídeo em alta definição.</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>Você pode consultar o <strong>resumo da triagem inteligente com IA</strong> antes e durante o atendimento.</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {isOnline && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Phone className="h-5 w-5 text-primary" />
              Aguardando Pacientes...
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                  <Bell className="h-8 w-8 text-primary animate-bounce" />
                </div>
                <p className="text-foreground font-medium">
                  Aguardando novos pacientes na fila expressa...
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Você receberá um sinal sonoro e aviso visual quando um paciente solicitar atendimento.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!isOnline && (
        <Card className="bg-card border-border">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <X className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-foreground font-semibold text-base">Você está Offline</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                {!isApproved
                  ? 'Complete o envio dos seus documentos para liberar os atendimentos do plantão express.'
                  : 'Clique em "Ficar Online" acima para iniciar o recebimento de pacientes em tempo real.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Onboarding & Compliance Documental */}
      <DoctorOnboardingModal
        open={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        doctor={profile as any}
      />
    </div>
  );
}

